/**
 * Session store — Zustand + expo-secure-store.
 *
 * Invariants (mobile CLAUDE.md §2):
 *  - Auth tokens are stored ONLY in expo-secure-store (not AsyncStorage, not memory-only).
 *  - No PII in plain storage; the token is opaque to the app layer.
 *
 * Shape: { token, user, status }
 *   status: 'loading' (restore in progress) | 'authenticated' | 'unauthenticated'
 *
 * On module load this file registers a token accessor with src/api/client.ts so
 * the API client can attach the Bearer token without importing this module
 * directly (avoids circular dep).
 */

import { create } from 'zustand';
import { Platform } from 'react-native';
import * as SecureStore from 'expo-secure-store';
import type { LoginResponse, MeResponse, SignupRequest } from '@beam/api-types';
import { login as apiLogin, me as apiMe, refresh as apiRefresh, logout as apiLogout, signup as apiSignup, deleteAccount as apiDeleteAccount, resetPassword as apiResetPassword, changePassword as apiChangePassword, setTokenAccessor, setUnauthorizedHandler, setRefreshHandler, setCsrfAccessor } from '../api/client';
import { disableWebPush } from '../push/registerWebPush';
import { enableNativePush, disableNativePush } from '../push/registerNativePush';
import { configureRevenueCat, logInRevenueCat, logOutRevenueCat } from '../billing/revenuecat';

const IS_WEB = Platform.OS === 'web';

// ---------------------------------------------------------------------------
// Secure-store key — one single key; must never change without migration.
// ---------------------------------------------------------------------------
const TOKEN_KEY = 'beam_auth_token';
const REFRESH_KEY = 'beam_refresh_token';

// ---------------------------------------------------------------------------
// W1 (web) — CSRF token persistence.
// The httpOnly `beam_refresh` cookie survives a full page reload, but the CSRF
// token (required on /refresh + /logout) lived in memory only. So on a cold load
// `restore()` called /api/auth/refresh with an EMPTY X-CSRF-Token → backend 403 →
// the user was bounced to /login on every refresh. Persist the token to localStorage
// (web only) so session restore can echo it. The refresh secret stays httpOnly;
// this is just the double-submit value, and it rotates on every refresh.
// ---------------------------------------------------------------------------
const CSRF_STORAGE_KEY = 'beam_web_csrf';
function persistWebCsrf(csrf: string | null): void {
  if (!IS_WEB || typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (csrf) window.localStorage.setItem(CSRF_STORAGE_KEY, csrf);
    else window.localStorage.removeItem(CSRF_STORAGE_KEY);
  } catch {
    // localStorage can throw (private mode / quota) — non-fatal.
  }
}
function readWebCsrf(): string | null {
  if (!IS_WEB || typeof window === 'undefined' || !window.localStorage) return null;
  try {
    return window.localStorage.getItem(CSRF_STORAGE_KEY);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// RevenueCat lifecycle (in-app subscriptions, 2026-06-19).
// Bind the RevenueCat purchaser to the BEAM org id on every successful auth so the
// RevenueCat → backend webhook resolves the License by org id. Safe to call on web —
// the platform-split stub (src/billing/revenuecat.web.ts) makes these no-ops there.
// ---------------------------------------------------------------------------
function bindRevenueCat(orgId: string | undefined): void {
  if (!orgId) return;
  void configureRevenueCat(orgId).catch(() => undefined);
  void logInRevenueCat(orgId).catch(() => undefined);
}

// ---------------------------------------------------------------------------
// Store types
// ---------------------------------------------------------------------------

export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated';

export type SessionUser = LoginResponse['user'];

interface SessionState {
  token: string | null;
  /** W1 (web) — the CSRF token to echo on /refresh + /logout. In-memory only; null on native. */
  csrf: string | null;
  user: SessionUser | null;
  status: SessionStatus;

  /** Call once on app launch (root layout). Loads token from secure-store and
   *  validates it against GET /api/auth/me. Clears state on any failure. */
  restore: () => Promise<void>;

  /** Email/password login. Stores token in secure-store, sets user. */
  login: (email: string, password: string) => Promise<void>;

  /** In-app signup (POST /api/public/signup). Creates a locked account and signs in immediately. */
  signup: (input: SignupRequest) => Promise<void>;

  /** Apply a LoginResponse obtained out-of-band (e.g. accept-invite). Stores token + user. */
  applyLogin: (response: LoginResponse) => Promise<void>;

  /** Complete a password reset with the emailed token → signs in (all prior sessions revoked). */
  resetPassword: (token: string, password: string) => Promise<void>;

  /** Change the signed-in user's password (verify current). Other devices are signed out; this
   *  device receives a fresh session and stays signed in. */
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;

  /** W0 — exchange the stored refresh token for a fresh access token. Returns the new token or null. */
  refresh: () => Promise<string | null>;

  /** Revoke the session server-side, clear secure-store, and reset state. */
  logout: () => Promise<void>;

  /**
   * Permanently delete the account (org + all data) via POST /api/account/delete, re-authenticating
   * with the current password. On success the local session is fully cleared (tokens wiped from
   * secure-store, RevenueCat logged out, push revoked) so the AuthGate routes to login/marketing.
   * Throws on failure (e.g. wrong password) so the screen can surface the backend error message.
   */
  deleteAccount: (password: string) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Local teardown — wipes everything this device knows about the session.
// Shared by logout() and deleteAccount() so a deleted account leaves no token,
// RevenueCat binding, or push subscription behind. Does NOT call the backend
// logout endpoint (the account-delete path has no session left to revoke).
// ---------------------------------------------------------------------------
async function clearLocalSession(): Promise<void> {
  persistWebCsrf(null);
  await disableWebPush().catch(() => undefined);
  await disableNativePush().catch(() => undefined);
  await logOutRevenueCat().catch(() => undefined); // detach the RevenueCat purchaser
  if (!IS_WEB) {
    await SecureStore.deleteItemAsync(TOKEN_KEY).catch(() => undefined);
    await SecureStore.deleteItemAsync(REFRESH_KEY).catch(() => undefined);
  }
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useSession = create<SessionState>((set, get) => ({
  token: null,
  csrf: null,
  user: null,
  status: 'loading',

  async restore() {
    try {
      if (IS_WEB) {
        // No persistent token store on web (the refresh token is an httpOnly cookie). Mint an access
        // token from it — success means a live session survived the reload.
        const resp = await apiRefresh();
        persistWebCsrf(resp.csrfToken ?? null);
        set({ token: resp.token, csrf: resp.csrfToken ?? null, user: resp.user, status: 'authenticated' });
        return;
      }
      const token = await SecureStore.getItemAsync(TOKEN_KEY);
      if (!token) {
        set({ token: null, user: null, status: 'unauthenticated' });
        return;
      }
      // Optimistically set the token so the client accessor returns it for /me (refresh-and-retry on 401).
      set({ token });
      const user: MeResponse = await apiMe();
      set({ token, user, status: 'authenticated' });
      bindRevenueCat(user.organizationId); // bind purchaser to org id on session restore
      void enableNativePush().catch(() => undefined); // §2.5 — (re)register this device for push

    } catch {
      if (!IS_WEB) await SecureStore.deleteItemAsync(TOKEN_KEY).catch(() => undefined);
      persistWebCsrf(null);
      set({ token: null, csrf: null, user: null, status: 'unauthenticated' });
    }
  },

  async login(email, password) {
    const client: 'web' | 'ios' | 'android' = IS_WEB ? 'web' : Platform.OS === 'android' ? 'android' : 'ios';
    const response = await apiLogin(email, password, client);
    if (IS_WEB) {
      persistWebCsrf(response.csrfToken ?? null);
      set({ token: response.token, csrf: response.csrfToken ?? null, user: response.user, status: 'authenticated' });
      return;
    }
    await SecureStore.setItemAsync(TOKEN_KEY, response.token);
    if (response.refreshToken) await SecureStore.setItemAsync(REFRESH_KEY, response.refreshToken);
    set({ token: response.token, user: response.user, status: 'authenticated' });
    bindRevenueCat(response.user.organizationId); // bind purchaser to org id on login
    void enableNativePush().catch(() => undefined); // §2.5 — register this device for push
  },

  async signup(input) {
    // POST /api/public/signup returns the same auth payload as login → sign in immediately.
    // The new account is created LOCKED (status=incomplete); the AuthGate routes it to the paywall.
    const response = await apiSignup(input);
    if (IS_WEB) {
      persistWebCsrf(response.csrfToken ?? null);
      set({ token: response.token, csrf: response.csrfToken ?? null, user: response.user, status: 'authenticated' });
      return;
    }
    await SecureStore.setItemAsync(TOKEN_KEY, response.token);
    if (response.refreshToken) await SecureStore.setItemAsync(REFRESH_KEY, response.refreshToken);
    set({ token: response.token, user: response.user, status: 'authenticated' });
    bindRevenueCat(response.user.organizationId); // bind purchaser to org id on signup
    void enableNativePush().catch(() => undefined); // §2.5 — register this device for push
  },

  async applyLogin(response) {
    if (IS_WEB) {
      persistWebCsrf(response.csrfToken ?? null);
      set({ token: response.token, csrf: response.csrfToken ?? null, user: response.user, status: 'authenticated' });
      return;
    }
    await SecureStore.setItemAsync(TOKEN_KEY, response.token);
    if (response.refreshToken) await SecureStore.setItemAsync(REFRESH_KEY, response.refreshToken);
    set({ token: response.token, user: response.user, status: 'authenticated' });
    bindRevenueCat(response.user.organizationId); // bind purchaser to org id (accept-invite path)
    void enableNativePush().catch(() => undefined); // §2.5 — register this device for push
  },

  async resetPassword(token, password) {
    const client: 'web' | 'ios' | 'android' = IS_WEB ? 'web' : Platform.OS === 'android' ? 'android' : 'ios';
    const response = await apiResetPassword(token, password, client);
    await get().applyLogin(response);
  },

  async changePassword(currentPassword, newPassword) {
    const client: 'web' | 'ios' | 'android' = IS_WEB ? 'web' : Platform.OS === 'android' ? 'android' : 'ios';
    const response = await apiChangePassword(currentPassword, newPassword, client);
    // Re-establish this device's session from the fresh tokens (other devices were revoked).
    await get().applyLogin(response);
  },

  async refresh() {
    try {
      if (IS_WEB) {
        const resp = await apiRefresh();
        persistWebCsrf(resp.csrfToken ?? null);
        set({ token: resp.token, csrf: resp.csrfToken ?? null, user: resp.user, status: 'authenticated' });
        return resp.token;
      }
      const rt = await SecureStore.getItemAsync(REFRESH_KEY);
      if (!rt) return null;
      const resp = await apiRefresh(rt);
      await SecureStore.setItemAsync(TOKEN_KEY, resp.token);
      if (resp.refreshToken) await SecureStore.setItemAsync(REFRESH_KEY, resp.refreshToken);
      set({ token: resp.token, user: resp.user, status: 'authenticated' });
      return resp.token;
    } catch {
      return null;
    }
  },

  async logout() {
    // §2.5 — revoke THIS device's push subscription as its session ends (no-op on native /
    // when push was never enabled). Best-effort; runs while still authed (needs the access token).
    if (IS_WEB) {
      await disableWebPush().catch(() => undefined);
      await logOutRevenueCat().catch(() => undefined);
      await apiLogout();
      persistWebCsrf(null);
      set({ token: null, csrf: null, user: null, status: 'unauthenticated' });
      return;
    }
    // Native: revoke this session server-side (best-effort) BEFORE wiping the refresh token.
    const rt = await SecureStore.getItemAsync(REFRESH_KEY).catch(() => null);
    await disableWebPush().catch(() => undefined);
    await disableNativePush().catch(() => undefined);
    await logOutRevenueCat().catch(() => undefined); // detach the RevenueCat purchaser
    await apiLogout(rt ?? undefined);
    await SecureStore.deleteItemAsync(TOKEN_KEY).catch(() => undefined);
    await SecureStore.deleteItemAsync(REFRESH_KEY).catch(() => undefined);
    set({ token: null, csrf: null, user: null, status: 'unauthenticated' });
  },

  async deleteAccount(password) {
    // Authenticated, owner-only. Throws on failure (wrong password, non-owner) so the screen can
    // show the backend error message. The call must run while still authed (it carries the token).
    const result = await apiDeleteAccount(password);
    if (!result.deleted) {
      throw new Error('The account could not be deleted. Please try again.');
    }
    // Account is gone server-side — tear down everything this device holds and route to login.
    await clearLocalSession();
    set({ token: null, csrf: null, user: null, status: 'unauthenticated' });
  },
}));

// ---------------------------------------------------------------------------
// Wire the token accessor into the API client.
// Must run at module-load time so the accessor is ready before any request.
// ---------------------------------------------------------------------------
setTokenAccessor(() => useSession.getState().token);
setCsrfAccessor(() => useSession.getState().csrf ?? readWebCsrf());

// On a 401 from any authed request (token expired/invalid), clear the session so the AuthGate
// redirects to /login instead of the screen dead-ending on "Token expired".
setUnauthorizedHandler(() => {
  if (useSession.getState().status !== 'unauthenticated') {
    void useSession.getState().logout();
  }
});

// W0 — let the API client refresh the access token (and rotate the refresh token) on a 401 before
// falling back to sign-out, so a valid session no longer hard-logs-out every 15 minutes.
setRefreshHandler(() => useSession.getState().refresh());
